import { useEffect, useMemo, useRef, useState } from 'react'

export interface StudioCommand {
  id: string
  label: string
  /** Grouping label (e.g. "Go to area", "Actions"). */
  group: string
  /** Optional secondary hint shown right-aligned. */
  hint?: string
  /** Disabled commands stay visible (discoverable) but are not runnable. */
  disabled?: boolean
  run: () => void
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  commands: StudioCommand[]
}

/**
 * The SPEC-017 ⌘K command palette (T022) — the keyboard-first fast lane for
 * jumping between studio areas and firing key actions. Fully operable by
 * keyboard (FR-A11Y): ↑/↓ move the active item, Enter runs it, Esc closes;
 * the dialog traps focus on the filter input and is labelled for screen
 * readers. Rendering nothing when closed keeps it out of the tab order.
 *
 * @param open - Whether the palette is visible.
 * @param onClose - Invoked on Esc, backdrop click, or after a command runs.
 * @param commands - The available commands (already scoped by the shell).
 */
export default function CommandPalette({ open, onClose, commands }: CommandPaletteProps): JSX.Element | null {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset the filter + selection each time the palette opens, and focus the input.
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
      // Focus after paint so the browser doesn't lose it to the mounting dialog.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
    return undefined
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    return commands.filter((c) => `${c.label} ${c.group} ${c.hint ?? ''}`.toLowerCase().includes(q))
  }, [commands, query])

  // Keep the active index in range as the filtered list shrinks.
  useEffect(() => { setActiveIndex(0) }, [query])

  if (!open) return null

  const runAt = (index: number): void => {
    const command = filtered[index]
    if (!command || command.disabled) return
    command.run()
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Enter') { e.preventDefault(); runAt(activeIndex) }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(20,16,8,0.45)] px-4 pt-[12vh] backdrop-blur-sm"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Studio command palette"
        className="lyrics-studio w-full max-w-xl overflow-hidden rounded-xl border border-[var(--ls-line-strong)] bg-[var(--ls-paper)] text-[var(--ls-ink)] shadow-[0_24px_60px_-24px_rgba(33,29,21,0.6)]"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-3 border-b border-[var(--ls-line)] px-4 py-3">
          <span aria-hidden="true" className="text-sm text-[var(--ls-gold-deep)]">⌘K</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to an area or run an action…"
            aria-label="Filter studio commands"
            role="combobox"
            aria-expanded="true"
            aria-controls="ls-command-list"
            aria-activedescendant={filtered[activeIndex] ? `ls-command-${filtered[activeIndex].id}` : undefined}
            className="w-full bg-transparent font-body text-[15px] text-[var(--ls-ink)] placeholder:text-[var(--ls-ink-muted)] focus:outline-none"
          />
        </div>

        <ul id="ls-command-list" role="listbox" aria-label="Studio commands" className="max-h-80 overflow-y-auto py-2">
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-[var(--ls-ink-muted)]">No matching commands.</li>
          )}
          {filtered.map((command, index) => (
            <li key={command.id} id={`ls-command-${command.id}`} role="option" aria-selected={index === activeIndex} aria-disabled={command.disabled}>
              <button
                type="button"
                disabled={command.disabled}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => runAt(index)}
                className={`flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45 ${
                  index === activeIndex ? 'bg-[var(--ls-gold-wash)]' : 'bg-transparent'
                }`}
              >
                <span className="flex flex-col">
                  <span className="font-body text-sm text-[var(--ls-ink)]">{command.label}</span>
                  <span className="text-[0.68rem] uppercase tracking-[0.14em] text-[var(--ls-ink-muted)]">{command.group}</span>
                </span>
                {command.hint && <span className="shrink-0 text-[0.7rem] text-[var(--ls-ink-muted)]">{command.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
